import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Eye, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { SectionHead } from "./Reveal";
import { waLink } from "@/lib/site";

const ITEMS = [
  { cat: "Healthcare", t: "Healthcare Clinic Concept", h: "Modern care, close to home.", img: "/images/clinic.jpg", d: "Doctor profiles, services, timings and an appointment enquiry form." },
  { cat: "Beauty & Spa", t: "Beauty Salon & Spa Concept", h: "Beauty, crafted slowly.", img: "/images/salon.jpg", d: "Treatment menus, gallery, offers and WhatsApp booking enquiries." },
  { cat: "Corporate", t: "Corporate Business Concept", h: "Built for ambitious teams.", img: "/images/corporate.jpg", d: "Company profile, services, team and lead-capture enquiry forms." },
  { cat: "Restaurant", t: "Restaurant & Cafe Concept", h: "Taste the evening.", img: "/images/restaurant.jpg", d: "Digital menu, reservation enquiries and location with directions." },
  { cat: "E-Commerce", t: "E-Commerce Store Concept", h: "Drop 07 — now live.", img: "/images/ecommerce.jpg", d: "Product catalogue, cart and payment gateway integration." },
  { cat: "Real Estate", t: "Real Estate Concept", h: "Homes that rise above.", img: "/images/realestate.jpg", d: "Property listings, floor plans, amenities and site-visit enquiries." },
  { cat: "Education", t: "Educational Institute Concept", h: "Where curious minds grow.", img: "/images/education.jpg", d: "Courses, admissions enquiry, faculty and campus gallery." },
  { cat: "Portfolio", t: "Personal Portfolio Concept", h: "Frames that tell stories.", img: "/images/portfolio.jpg", d: "Personal brand, featured work, about section and contact." },
];
const CATS = ["All", ...ITEMS.map((i) => i.cat)];

const Browser = ({ item, large }) => (
  <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b0f1c]">
    <div className="flex items-center gap-1.5 border-b border-white/5 px-3 py-2.5">
      <span className="h-2 w-2 rounded-full bg-[#ff5f57]" /><span className="h-2 w-2 rounded-full bg-[#febc2e]" /><span className="h-2 w-2 rounded-full bg-[#28c840]" />
      <span className="ml-3 truncate rounded-md bg-white/5 px-3 py-0.5 text-[10px] text-slate-400">demo-concept / {item.cat.toLowerCase()}</span>
    </div>
    <div className={`relative overflow-hidden ${large ? "aspect-[16/9]" : "aspect-[16/11]"}`}>
      <img src={item.img} alt={`${item.t} website mockup`} loading="lazy" className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
      <div className="absolute left-[7%] top-1/2 max-w-[70%] -translate-y-1/2">
        <p className="text-[9px] uppercase tracking-[0.3em] text-[var(--gold-2)]">{item.cat}</p>
        <p className={`font-display mt-1 font-semibold leading-tight text-white ${large ? "text-3xl sm:text-4xl" : "text-xl"}`}>{item.h}</p>
        <span className="mt-3 inline-block rounded-full bg-white px-3 py-1 text-[9px] font-semibold text-black">Learn more</span>
      </div>
    </div>
  </div>
);

export const Portfolio = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const list = cat === "All" ? ITEMS : ITEMS.filter((i) => i.cat === cat);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Our Portfolio" title={<>Design concepts for <span className="text-gold-grad italic">your industry</span>.</>} intro="These are sample website concepts created by Pratap Group to demonstrate our design style — they are demonstrations, not completed client projects." />
        <div className="mt-10 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none]" role="tablist">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`portfolio-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${cat === c ? "text-black" : "text-slate-300 hover:text-white"}`}>
              {cat === c && <motion.span layoutId="pf-pill" className="absolute inset-0 rounded-full bg-[var(--gold)]" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
              <span className="relative">{c}</span>
            </button>
          ))}
        </div>
        <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {list.map((item) => (
              <motion.button
                layout
                key={item.t}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setActive(item)}
                data-testid={`portfolio-card-${item.cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group text-left"
              >
                <div className="relative transition-transform duration-500 group-hover:-translate-y-1.5">
                  <Browser item={item} />
                  <span className="absolute right-3 top-12 rounded-full border border-gold/50 bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--gold-2)] backdrop-blur">Demo Concept</span>
                  <span className="absolute inset-x-0 bottom-0 flex translate-y-2 items-center justify-center gap-2 rounded-b-2xl bg-gradient-to-t from-black/90 to-transparent pb-5 pt-12 text-sm font-semibold text-white opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    <Eye className="h-4 w-4 text-[var(--gold)]" /> Preview concept
                  </span>
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <h3 className="font-display text-base font-semibold text-white">{item.t}</h3>
                  <span className="shrink-0 text-xs text-slate-400">{item.cat}</span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="portfolio-preview-dialog" className="max-w-3xl border-white/10 bg-[var(--navy)] p-5 text-white sm:p-7">
          {active && (
            <>
              <Browser item={active} large />
              <div className="mt-2 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[var(--gold)]">Demo Concept · Not a client project</span>
                  <DialogTitle className="font-display mt-2 text-2xl">{active.t}</DialogTitle>
                  <DialogDescription className="mt-2 text-sm text-slate-300">{active.d}</DialogDescription>
                </div>
                <a href={waLink(`Hello Pratap Group, I liked your ${active.t}. I would like a similar website. Please share details and pricing.`)} target="_blank" rel="noopener noreferrer" data-testid="portfolio-preview-whatsapp-button" className="btn-gold inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold">
                  Get a similar website <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
